import PubSub from '~/utils/PubSub/PubSub';
import Store from '~/store/Store';
import Logger from '~/utils/logger/logger';
import SocketEventType from '~/constants/socket-event-type';

export class WebSocketService extends PubSub {
  // Expected like wss://somewhere.husqvarnagroup.net/?token=
  endpoint = String(process.env.ENDPOINT_WS);
  heartBeatInterval: NodeJS.Timeout | null = null;
  heartBeatUuid: string | null = null;
  socket: WebSocket | undefined;
  fetchingBffToken: boolean | undefined;
  connectionFailures = 0;
  connectionsCount = 0;
  halted = false;

  initHeartBeat = () => {
    const intervalMs = this.getHeartBeatIntervalMs();
    Logger.log(`HeartBeat init, ${intervalMs}`);
    if (this.heartBeatInterval) {
      clearInterval(this.heartBeatInterval);
    }
    this.heartBeatUuid = null;
    this.heartBeatInterval = setInterval(this.runHeartBeat, intervalMs);
    this.runHeartBeat();
  };

  getHeartBeatIntervalMs = () => {
    const envInterval = Number(process.env.WS_HEARTBEAT_INTERVAL_SECONDS);
    if (isNaN(envInterval) || !envInterval) {
      return 20_000;
    }
    return envInterval * 1000;
  };

  runHeartBeat = async () => {
    if (this.heartBeatUuid) {
      // Previously sent UUID has not been cleared yet, this likely means that reply was not received.
      this.closeConnection('HB failure');
      await this.connectIfLoggedIn();
    } else {
      this.heartBeatUuid = window.crypto.randomUUID();
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'HEARTBEAT', id: this.heartBeatUuid }));
        Logger.log('HB sent');
      } else {
        Logger.error('WebSocket is not open. Cannot send heartbeat.');
      }
    }
  };

  constructor(private fetchFunction: () => Promise<string | undefined>) {
    super();
  }

  connect = async () => {
    if (this.halted) {
      return;
    }
    if (!this.fetchingBffToken) {
      this.fetchingBffToken = true;
      let token;
      try {
        token = await this.fetchFunction();
      } catch (error: any) {
        Logger.log('Failed to fetch WS token');
      } finally {
        this.fetchingBffToken = false;
      }
      if (this.halted) {
        return;
      }
      if (token) {
        this.socket = new WebSocket(this.endpoint + token);

        // Connection opened
        this.socket.addEventListener('open', this.onConnect);

        // Listen for messages
        this.socket.addEventListener('message', this.onMessage);
        this.socket.addEventListener('close', this.onDisconnect);
        this.socket.addEventListener('error', this.onError);
      } else {
        Logger.log('Empty WS token');
      }
    }
  };

  onConnect = () => {
    Logger.log('SocketService connected');
    if (this.connectionsCount > 0) {
      // Treat this as reconnection
      this.emit(SocketEventType.Reconnection);
    }
    this.connectionsCount += 1;
    this.connectionFailures = 0;
    this.initHeartBeat();
  };

  onMessage = (msg) => {
    const data = JSON.parse(msg.data);
    if (data.type === 'HEARTBEAT') {
      this.onHeartBeat(data?.id);
    } else {
      Logger.log('onMessage', data.type, data);
      this.emit(data.type, data);
    }
  };

  onHeartBeat = (uuid) => {
    if (uuid === this.heartBeatUuid) {
      this.heartBeatUuid = null;
      Logger.log('HB response received successfully');
    } else {
      Logger.error('HB response UUID mismatch');
    }
  };

  onDisconnect = async (msg) => {
    Logger.log('WebSocketService disconected: ' + msg);
    this.connectionFailures += 1;
    const reconnectionTimeoutSec = this.connectionFailures > 5 ? 30 : this.connectionFailures * 5;
    if (!this.heartBeatUuid) {
      // Startup failure
      setTimeout(this.connect, 1000 * reconnectionTimeoutSec);
    }
  };

  onError = (...args) => {
    Logger.error('WebSocketService error:', ...args);
  };

  disconnect() {
    this.closeConnection();
  }

  /** Stops this service's socket and timers so another connection can take over. */
  halt() {
    this.halted = true;
    if (this.heartBeatInterval) {
      clearInterval(this.heartBeatInterval);
      this.heartBeatInterval = null;
    }
    this.heartBeatUuid = null;
    if (this.socket) {
      this.socket.removeEventListener('open', this.onConnect);
      this.socket.removeEventListener('message', this.onMessage);
      this.socket.removeEventListener('close', this.onDisconnect);
      this.socket.removeEventListener('error', this.onError);
      this.closeConnection('halted');
      this.socket = undefined;
    }
  }

  /** Re-enables `connect()`; the next open emits `Reconnection` to resync the gap. */
  unhalt() {
    this.halted = false;
    this.connectionsCount = Math.max(this.connectionsCount, 1);
  }

  closeConnection(reason?: string) {
    if (this.socket) {
      Logger.log('WebSocketService disconnecting' + (reason ? ': ' + reason : ''));
      try {
        this.socket.close();
      } catch (error: any) {
        Logger.error(error);
      }
    }
  }

  getIsAuthAvailable = () => {
    return (
      Store.getState().authentication.companyId &&
      Store.getState().authentication.companyId !== '' &&
      Store.getState().authentication.authenticated
    );
  };

  connectIfLoggedIn = async () => {
    if (this.getIsAuthAvailable()) {
      await this.connect();
    }
  };
}

let websocketService: WebSocketService;
const getWebsocketService = (fetchFunction: () => Promise<string | undefined>) => {
  if (!websocketService) {
    websocketService = new WebSocketService(fetchFunction);
  }
  return websocketService;
};
export { getWebsocketService };
